import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@/app/providers/auth-context'
import { ButtonLink } from '@/components/ui/ButtonLink'
import { keepsStorefrontClub, storefrontReturnTarget } from '@/features/clubs/storefrontReturn'
import { useStaffClub } from '@/features/players/useStaffClub'
import { startClubCtaHandler } from '@/lib/analytics/startClubCta'
import { PublicAccountMenu } from './PublicAccountMenu'
import { PublicMobileSheet } from './PublicMobileSheet'
import { StorefrontReturn } from './StorefrontReturn'
import {
  WORKSPACE_CTA,
  publicModeConfig,
  type PublicMode,
  type PublicNavLink,
} from './publicNav'
import styles from './PublicHeader.module.css'

type Props = { mode: PublicMode }

// In-app top bar (coexists with the operational sidebar, which already carries
// the app destinations). EMPTY BY THE OWNER'S CALL (2026-09-06): the workspace
// menu he described has no Guides in it, and Guides was the only outward link
// this bar carried, so the bar is now brand + account. Putting it back is one
// entry — `{ label: 'Guides', to: '/guides' }` — which is why the list stayed a
// list. The marketing menu itself lives on the public front-of-house header and
// is untouched.
const APP_OUTWARD_LINKS: PublicNavLink[] = []

/**
 * The horizontal top-bar link row, and the ONE place a mode's `navLinks` are drawn.
 *
 * DRAWS NOTHING WHEN THE LIST IS EMPTY, which is now the shipped state for two of
 * the three bars that call it: the in-app bar (above) and, since 2026-09-07, every
 * player surface (publicNav's PLAYER — marketing chrome belongs to the marketing
 * site). An empty `<nav>` is not harmless — it is a landmark a screen reader
 * announces and then finds nothing inside — so the element goes with its links.
 */
function OutwardNav({ links, label }: { links: PublicNavLink[]; label: string }) {
  if (links.length === 0) return null
  return (
    <nav className={styles.desktopNav} aria-label={label}>
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          className={({ isActive }) =>
            `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`.trim()
          }
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  )
}

/** The three bars, shared with StorefrontHeader — which also borrows this module's
 *  `.hamburger` class, so the button and its glyph come from one place. */
export function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The two-mode public top header (site-relaunch Wave 1). `mode` comes from
 * resolveChrome: marketing (B2B/brand + resources), player (marketplace), or
 * auth (logo-only, no nav). Fixed top bar; exactly one primary CTA per screen.
 * Desktop shows brand + nav + auth cluster; mobile collapses nav into a
 * top-anchored sheet. The cluster is the same at every width: signed out, an
 * outlined Sign in beside the filled primary (docs/ux/2026-10-20-home-player-entry.md
 * §3 D — a player must find Sign in on a phone's first screen); signed in, the
 * account menu. Signed out, phones narrow the brand to make room: two lines below
 * 480px, the TRC monogram below 360px. Never flashes the signed-out cluster while
 * auth is `checking` (reserves a skeleton the pair's width).
 */
export function PublicHeader({ mode }: Props) {
  const { state, isAnyClubStaff } = useAuth()
  const { pathname } = useLocation()
  const staffClub = useStaffClub()
  const [sheetOpen, setSheetOpen] = useState(false)
  const hamburgerRef = useRef<HTMLButtonElement | null>(null)

  // Close the sheet on route change (mirrors AppShell's drawer auto-close).
  useEffect(() => {
    setSheetOpen(false)
  }, [pathname])

  const isAuthed = state === 'authenticated'
  const isChecking = state === 'checking'

  // Auth-minimal chrome: wordmark only, no nav, no cluster, no hamburger.
  if (mode === 'auth') {
    return (
      <header className={styles.header}>
        <div className={`container ${styles.bar} ${styles.barAuth}`}>
          <Link to="/" className={styles.brand}>
            The Racket Center
          </Link>
        </div>
      </header>
    )
  }

  // In-app top bar (coexists with the operational sidebar; desktop-only — the
  // AppShell slot hides it below 1024, where the merged AppDrawer takes over).
  // Full-bleed bar so the brand aligns with the sidebar column; the account
  // avatar, no primary CTA, always signed-in. The avatar's menu is the SAME menu
  // as everywhere else (owner, 2026-09-06) — Manage included — and since
  // 2026-09-07 it is the ONLY place Workspace / Admin appear at this width: the
  // sidebar dropped its own Manage group rather than say it twice. So this
  // header is load-bearing for reach, not decoration; see AppNavLinks.
  if (mode === 'app') {
    // THE BOARD KEEPS THE CLUB (/ux storefront-exits §3.1): "Find a game" on a storefront
    // lands here, on app chrome, and it is the storefront menu's own row. Only the board —
    // the workspace, the profile and the rest of the app are the viewer's, and keep ours.
    const boardReturn = keepsStorefrontClub(pathname) ? storefrontReturnTarget(pathname) : null
    return (
      <header className={styles.header}>
        <div className={styles.bar}>
          {boardReturn ? (
            <StorefrontReturn target={boardReturn} />
          ) : (
            <Link to="/" className={styles.brand}>
              The Racket Center
            </Link>
          )}
          <OutwardNav links={APP_OUTWARD_LINKS} label="Site" />
          <div className={styles.cluster}>
            {isChecking ? (
              <span className={styles.clusterSkeletonAvatar} aria-hidden="true" />
            ) : isAuthed ? (
              <PublicAccountMenu />
            ) : null}
          </div>
        </div>
      </header>
    )
  }

  const config = publicModeConfig(mode)
  // TOP HEADER = the marketing menu for BOTH signed-in and signed-out viewers
  // (owner IA 2026-07-22) — but ONLY on the marketing surfaces since 2026-09-07:
  // player mode's list is empty, so /clubs, /players, /tournaments and /ladders
  // draw brand + hamburger + the auth cluster and no company bar. App navigation
  // is in the hamburger drawer at every width; account + Manage (Workspace /
  // Admin) live in the avatar dropdown.
  const navLinks = config.navLinks
  const primary = config.signedOutPrimary
  const canManage = isAuthed && isAnyClubStaff()

  // THE CLUB THIS TAB WALKED OUT OF, on the player surfaces and nowhere else. `null` is
  // the ordinary answer — arrived from search, from a forwarded link, in a fresh tab (a
  // reload in the same tab keeps the club, owner 2026-10-16) — and it renders the bar exactly as it is today, the bare wordmark
  // this line used to be. When there IS a club, the club TAKES that slot: the wordmark
  // is not drawn beside it (owner, 2026-09-07), so the bar names one thing and it is the
  // club. Marketing pages never ask: a club is a landmark for someone browsing the
  // marketplace, not for someone reading our pricing. The pathname goes in because one
  // refusal belongs to the address rather than to the chrome — a club's own page must
  // never wear another club's mark, not even while it loads.
  // A STAFF MEMBER'S OWN CLUB beats that memory: a card opened from the club workspace names that club
  // and hides "Go to workspace" (docs/ux/2026-11-02-player-card-club-header.md).
  const clubReturn = mode === 'player' ? storefrontReturnTarget(pathname) : null
  const workspaceClub = mode === 'player' ? staffClub : null

  return (
    <>
      <header className={styles.header}>
        <div className={`container ${styles.bar} ${isAuthed ? '' : styles.barSignedOut}`.trim()}>
          <button
            type="button"
            ref={hamburgerRef}
            className={styles.hamburger}
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          >
            <HamburgerIcon />
          </button>

          {workspaceClub ? (
            <StorefrontReturn
              target={{
                slug: '',
                name: workspaceClub.name,
                logoUrl: '',
                href: `/organization?orgId=${encodeURIComponent(workspaceClub.orgId)}`,
              }}
              switcher={false}
            />
          ) : clubReturn ? (
            <StorefrontReturn target={clubReturn} />
          ) : (
            <Link to={config.brandTo} className={styles.brand}>
              <span className={styles.wordmark}>The Racket Center</span>
              <span className={styles.monogram} aria-hidden="true">
                TRC
              </span>
            </Link>
          )}

          <OutwardNav links={navLinks} label="Primary" />

          <div className={styles.cluster}>
            {isChecking ? (
              <span
                className={`${styles.clusterSkeleton} ${mode === 'player' ? styles.clusterSkeletonPlayer : ''}`.trim()}
                aria-hidden="true"
              />
            ) : isAuthed ? (
              <>
                {canManage && !workspaceClub ? (
                  <ButtonLink
                    to={WORKSPACE_CTA.to}
                    size="sm"
                    variant="primary"
                    className={styles.clusterPrimaryDesktop}
                  >
                    {WORKSPACE_CTA.label}
                  </ButtonLink>
                ) : null}
                <PublicAccountMenu />
              </>
            ) : (
              <>
                <ButtonLink
                  to={config.signInTo}
                  size="sm"
                  variant="secondary"
                  className={styles.clusterButton}
                >
                  Sign in
                </ButtonLink>
                <ButtonLink
                  to={primary.to}
                  size="sm"
                  variant="primary"
                  className={styles.clusterButton}
                  onClick={startClubCtaHandler(primary.ctaSource)}
                >
                  {primary.shortLabel ? (
                    <>
                      <span className={styles.labelFull}>{primary.label}</span>
                      <span className={styles.labelShort}>{primary.shortLabel}</span>
                    </>
                  ) : (
                    primary.label
                  )}
                </ButtonLink>
              </>
            )}
          </div>
        </div>
      </header>

      <PublicMobileSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        mode={mode}
        returnFocusRef={hamburgerRef}
      />
    </>
  )
}
